import { QueryClient } from "@tanstack/react-query"
import { createRouter, type NavigateOptions } from "@tanstack/react-router"
import { observable, runInAction } from "mobx"

import { RpcTimeoutException } from "@/lib/client/types.ts"
import { GlobalContextImpl } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { createAuthController } from "@/lib/oauth/AuthController.ts"
import { ErrorScene } from "@/routes/errors/Error.tsx"
import { NotFoundScene } from "@/routes/errors/NotFound.tsx"
import { routeTree } from "@/routes/routeTree.ts"

const MATTER_HOST = import.meta.env.VITE_MATTER_HOST || "http://localhost:8088"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount: number, error: Error) => {
        return failureCount < 3 && error instanceof RpcTimeoutException
      },
      throwOnError: true
    }
  }
})

const authController = createAuthController({
  spaceHost: MATTER_HOST,
  queryClient: queryClient,
})

const routerLog = getLogger("Router")

export function createApp() {
  const globalContext = new GlobalContextImpl({
    host: MATTER_HOST,
    queryClient,
    authController,
  })

  const router = createRouter({
    context: globalContext,
    routeTree: routeTree,
    defaultPreload: false, // We'd like to use "intent" in the future, but it's annoying in development mode and dangerous for untested use in production mode (eternal redirects)
    defaultViewTransition: false, // We'd like to enable it in the future, but for now it's masking issues with the implementation (excessive re-rendering)
    defaultErrorComponent: ErrorScene,
    defaultNotFoundComponent: NotFoundScene,
    scrollRestoration: false // We'd like to enable it in the future, right now the UI is not ready for that (batch lists don't restore their state)
  })

  // Patch the globalContext with the actual buildLocation function after router creation
  globalContext.buildLocation = (opts) => router.buildLocation(opts)

  const routerResolved = observable.box(false)

  const unsubscribe = router.subscribe("onLoad", () => {
    runInAction(() => routerResolved.set(true))
    unsubscribe()
  })

  router.subscribe("onBeforeNavigate", () => {
    routerLog.debug(`Routing to ${router.state.location.href}`)
  })

  return { globalContext, router, routerResolved }
}

export type AppInit = ReturnType<typeof createApp>
export type RouterType = AppInit["router"]
export type MatterLocation = NavigateOptions<RouterType>

declare module "@tanstack/react-router" {
  interface Register {
    router: RouterType
  }

  interface HistoryState {
    closeLocation?: MatterLocation
  }
}